// Liquid-first: this file synchronises quantity/remove on engraving PAIRS
// (watch line + engraving-fee line joined by the hidden "_Engraving Ref"
// property) — intercepting the quantity-selector and remove events BEFORE
// the theme's own handlers, writing both lines via /cart/change.js keyed by
// current line keys, morphing the returned sections and emitting accurate
// cart itemCount updates. Liquid alternatives were ruled out because this
// needs event interception before the theme's handlers, async cart writes
// and live cart JSON reads — none of which a server-rendered template can
// observe or perform.
//
// Forest 2026-09-09 — engraving upgrade, preview-vehicle-only companion to
// blocks/ai_gen_block_custom_engraving.liquid.
//
// SCOPE: EXPLICIT validated pair actions only. This helper NEVER makes a
// passive or quiet cart mutation after a generic cart update — a background
// repair once raced the PDP's two-leg edit and deleted a legitimate fee
// mid-edit. A pair is acted on only when a customer event names it and its
// identity is exact (ONE watch line carrying "Engraved with", ONE line of
// the real engraving-service variant, same whole ref, same engraved words,
// clicked row key belonging to one of the two). The WATCH leg is written
// first so a stock refusal can never leave a fee charged against a watch
// that could not move.
//
// DISCOUNT PARTITIONS (2026-09-10, proved against the real shop). Forest's
// automatic "Make it a pair, save 20%" splits ONE engraved watch at quantity 2
// into TWO lines of quantity 1 with IDENTICAL full properties and different
// keys. That is valid, not ambiguity. So a pair is a pair of GROUPS: every line
// under the ref with the same role, the same variant, the same selling plan and
// byte-identical properties, counted by aggregate quantity. Lines under the ref
// that disagree on any of those are still refused.
//
// The same probe showed Shopify MERGING those two lines back into one of
// quantity 3 when one of them was raised, and RE-KEYING unrelated lines (a
// plain watch, another engraving's rows) in the same response. So every write
// is followed by a fresh read, and no key is ever carried across a write. A
// clicked row is applied as a DELTA to the group total, which is why removing
// one row of a split pair takes that row's units and not the whole group.
//
// EVERY write is recorded as a pending operation BEFORE it is sent and is
// cleared only when a fresh read proves the pair reached either the quantity
// the customer asked for or its verified baseline. Anything else stays
// locked: the next customer action settles that operation before it may
// start a new one, and the cart shows a status line with a retry.
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

// The engraving service. Identity is this variant and nothing else: workshop
// font properties are copyable through the cart API, so a line carrying
// "Font"/"_Font Key" is NOT thereby a fee.
const SERVICE_VARIANT_ID = 44211286868221;
const PDP_SELECTOR = '[data-engraving-variant-id]';
const PDP_POLL_MS = 200;
const PENDING_KEY = 'fw-eng-cart-pending-v1';

// Every way out of the bag. The checkout button is rendered OUTSIDE the cart
// form and wired to it with form="cart-form", so the click and the form submit
// are two separate entry points and Enter reaches the form without either.
const CHECKOUT_SEL = '[name="checkout"], .cart__checkout-button, .additional-checkout-buttons, shopify-accelerated-checkout-cart, .shopify-payment-button';
const CHECKOUT_LOCK_SEL = '[name="checkout"], .cart__checkout-button, .additional-checkout-buttons, shopify-accelerated-checkout-cart';
const CART_FORM_SEL = 'form.cart-form, form[id="cart-form"]';

class FWEngravingCart {
  constructor() {
    this.busy = false;          // this helper's own transaction lock
    this.queued = null;         // latest { ref, qty, rowKey } while a sync runs
    this.pendingOp = null;      // unverified operation; exclusive until settled
    this.lastKnownCount = null; // last item_count actually read from the cart
    this.bound = false;
    this._waitTimer = 0;
    this._status = null;
    this._retryIntent = null;   // an intent whose cart READ failed; safe to re-run
  }

  attach() {
    if (this.bound) return;
    this.bound = true;
    this.pendingOp = this.readPending();

    // 🚨 WINDOW capture, not document capture. assets/component.js registers
    // the theme's on:click command router at DOCUMENT capture and loads in
    // <head>, so it is registered BEFORE this file — a later document-capture
    // listener cannot stop its remove write, because listeners on the same
    // node in the same phase run in registration order. The capture phase
    // reaches Window first, which is the only stage guaranteed to be earlier.
    // document is bound too, as the fallback for environments where window
    // takes no listeners; a per-event marker keeps a shared event single-handled.
    const win = typeof window !== 'undefined' && window && typeof window.addEventListener === 'function' ? window : null;
    if (win) this.bindRoot(win);
    if (document && typeof document.addEventListener === 'function') this.bindRoot(document);

    // Retry lives on the rendered status line, which morphs with the cart
    // section, so it is delegated rather than bound to the element.
    if (document && typeof document.addEventListener === 'function') {
      document.addEventListener('click', (e) => {
        const t = e.target;
        const btn = t && t.closest ? t.closest('[data-fw-eng-retry]') : null;
        if (!btn) return;
        e.preventDefault();
        this.retryPending();
      });
    }

    if (this.pendingOp) this.reviewPendingOnLoad();
  }

  bindRoot(root) {
    // CAPTURE: Horizon's cart-items-component listens on bubble and its
    // command router listens at document capture, so claiming the event here
    // gives the pair sole control of its own rows without touching any other
    // product's behaviour.
    root.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      (e) => this.onQuantityUpdate(e),
      { capture: true }
    );
    root.addEventListener(
      'click',
      (e) => this.onClick(e),
      { capture: true }
    );
    root.addEventListener(
      'submit',
      (e) => this.onSubmit(e),
      { capture: true }
    );
  }

  claim(e) {
    // One event, one handler, whichever propagation stage reaches it first.
    if (!e || e.__fwEngravingClaimed) return false;
    e.__fwEngravingClaimed = true;
    return true;
  }

  rowForEvent(e) {
    const t = e.target;
    if (!t || !t.closest) return null;
    const row = t.closest('tr[data-engraving-ref]');
    if (!row) return null;
    const ref = (row.dataset.engravingRef || '').trim();
    return ref ? row : null;      // a ref-less row keeps Horizon's native path
  }

  onQuantityUpdate(e) {
    const row = this.rowForEvent(e);
    if (!row) return;
    const qty = parseInt(e.detail && e.detail.quantity, 10);
    if (isNaN(qty) || qty < 0) return;
    if (!this.claim(e)) return;
    e.preventDefault();
    e.stopPropagation();          // cart-items-component never sees pair rows
    this.request(String(row.dataset.engravingRef), qty, row.dataset.key || null);
  }

  // TRUE while this helper is mid-write, while it holds an operation it could
  // not verify, or while the PDP block holds the shared transaction. Checking
  // out in any of those states would buy a bag whose real contents nobody can
  // state — including, in the worst case, a watch with no engraving against it.
  lockedForCheckout() {
    return this.busy || !!this.pendingOp || this.pdpLocked();
  }

  onClick(e) {
    if (this.lockedForCheckout()) {
      const co = e.target && e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
      if (co) {
        if (!this.claim(e)) return;
        e.preventDefault();
        e.stopPropagation();
        this.setStatus(this.pendingOp ? 'unconfirmed' : 'working', !!this.pendingOp);
        return;
      }
    }
    this.onRemoveClick(e);
  }

  // The cart's own POST is the last bypass: each row's quantity field is named
  // updates[], and the quantity selector only publishes a typed value on BLUR,
  // so Enter can serialise a new watch quantity while the fee still holds the
  // old one. Nothing native is allowed to post an engraving pair — an edited
  // pair row is settled through the paired path first.
  onSubmit(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches(CART_FORM_SEL)) return;
    if (this.lockedForCheckout()) {
      if (!this.claim(e)) return;
      e.preventDefault();
      e.stopPropagation();
      this.setStatus(this.pendingOp ? 'unconfirmed' : 'working', !!this.pendingOp);
      return;
    }
    const intents = this.editedPairIntents(form);
    if (!intents.length) return;
    if (!this.claim(e)) return;
    e.preventDefault();
    e.stopPropagation();
    this.setStatus('synced');
    this.requestAll(intents);
  }

  // Pair rows whose typed quantity no longer matches the quantity the page was
  // rendered with. defaultValue is the rendered value attribute, so this is
  // "what the customer typed" against "what the bag says".
  editedPairIntents(form) {
    const out = [];
    let rows;
    try { rows = form.querySelectorAll('tr[data-engraving-ref]'); } catch (e) { return out; }
    Array.prototype.forEach.call(rows || [], (row) => {
      const ref = (row.dataset && row.dataset.engravingRef ? row.dataset.engravingRef : '').trim();
      if (!ref) return;
      // Matched by property, not by selector: the field's name is literally
      // `updates[]`, and square brackets inside an attribute selector are a
      // trap in more than one CSS parser.
      let input = null;
      const inputs = row.querySelectorAll ? row.querySelectorAll('input') : [];
      Array.prototype.forEach.call(inputs || [], (el) => {
        if (input) return;
        const nm = (el.getAttribute && el.getAttribute('name')) || el.name || '';
        if (String(nm) === 'updates[]') input = el;
      });
      if (!input) return;
      const typed = parseInt(String(input.value).trim(), 10);
      const rendered = parseInt(String(input.defaultValue).trim(), 10);
      if (isNaN(typed) || typed < 0) return;
      if (!isNaN(rendered) && typed === rendered) return;
      if (out.some((i) => i.ref === ref)) return;
      out.push({ ref: ref, qty: typed, rowKey: (row.dataset && row.dataset.key) || null });
    });
    return out;
  }

  requestAll(intents) {
    this._batch = (this._batch || []).concat(intents || []);
    this.drainBatch();
  }

  drainBatch() {
    if (this.busy || !this._batch || !this._batch.length) return;
    const next = this._batch.shift();
    this.request(next.ref, next.qty, next.rowKey);
  }

  // Interception is the guard that actually holds; this makes the same state
  // visible and keyboard-unreachable. It is re-applied after every section
  // morph, because morphing replaces these elements outright.
  applyCheckoutLock() {
    const locked = this.lockedForCheckout();
    let nodes = [];
    try { nodes = Array.prototype.slice.call(document.querySelectorAll(CHECKOUT_LOCK_SEL)); } catch (e) { return; }
    nodes.forEach((el) => {
      if (typeof el.setAttribute !== 'function' || typeof el.hasAttribute !== 'function') return;
      const marked = el.hasAttribute('data-fw-eng-locked');
      if (locked && !marked) {
        el.setAttribute('data-fw-eng-locked', '');
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('inert', '');
      } else if (!locked && marked) {
        el.removeAttribute('data-fw-eng-locked');
        el.removeAttribute('aria-disabled');
        el.removeAttribute('inert');
      }
    });
  }

  onRemoveClick(e) {
    const row = this.rowForEvent(e);
    if (!row) return;
    const btn = e.target.closest('.cart-items__remove');
    if (!btn) return;
    if (!this.claim(e)) return;
    e.preventDefault();
    e.stopPropagation();
    this.request(String(row.dataset.engravingRef), 0, row.dataset.key || null);
  }

  // TRUE while the PDP engraving block holds the shared transaction: either
  // mid add/edit (isSubmitting) or sitting on an operation of its own that it
  // could not verify (fwPendingOp). A quantity write during either would see
  // a half-moved pair. There is deliberately NO request counter and NO
  // timeout that eventually grants permission to write — a bounded wait is
  // just a slower way of writing into someone else's transaction.
  pdpLocked() {
    try {
      const els = document.querySelectorAll ? document.querySelectorAll(PDP_SELECTOR) : [];
      for (let i = 0; i < els.length; i++) {
        if (els[i].isSubmitting) return true;
        if (els[i].fwPendingOp) return true;
      }
      return false;
    } catch (e) { return false; }
  }

  // One sync at a time. A click during a sync replaces the queued intent, it
  // never starts a parallel mutation. While the PDP engine holds the lock the
  // latest intent is RETAINED and re-polled — the poll reschedules itself for
  // as long as the PDP is busy, so an intent is never silently stranded.
  request(ref, qty, rowKey) {
    const intent = { ref: String(ref), qty: qty, rowKey: rowKey || null };
    if (this.busy) {
      this.queued = intent;
      return;
    }
    if (this.pdpLocked()) {
      this.queued = intent;
      this.setStatus('wait');
      this.waitForPdp();
      return;
    }
    this.busy = true;
    this.applyCheckoutLock();
    this.run(intent).catch(() => {}).finally(() => {
      this.busy = false;
      this.applyCheckoutLock();
      const next = this.queued;
      if (next) {
        this.queued = null;
        this.request(next.ref, next.qty, next.rowKey);
        return;
      }
      this.drainBatch();
    });
  }

  waitForPdp() {
    if (this._waitTimer || typeof setTimeout !== 'function') return;
    this._waitTimer = setTimeout(() => {
      this._waitTimer = 0;
      if (this.busy) { this.waitForPdp(); return; }   // re-check once free
      const next = this.queued;
      if (!next) return;
      if (this.pdpLocked()) { this.waitForPdp(); return; }
      this.queued = null;
      this.clearStatus();
      this.request(next.ref, next.qty, next.rowKey);
    }, PDP_POLL_MS);
  }

  async run(intent) {
    // An unresolved earlier operation settles FIRST. A new intent on top of a
    // pair whose real state is unknown is how mismatches get compounded.
    if (this.pendingOp) {
      const settled = await this.settle(this.pendingOp);
      if (!settled) {
        this.setStatus('unconfirmed', true);
        await this.announceFromFresh();
        return;
      }
      this.clearPending();
    }

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (e) {
      // The read failed, so nothing was written and the intent is still good.
      // Retry re-runs exactly this intent.
      this._retryIntent = intent;
      this.setStatus('read', true);
      await this.refreshRows();
      return;
    }
    if (!this.usable(cart)) {
      this._retryIntent = intent;
      this.setStatus('read', true);
      await this.refreshRows();
      return;
    }
    this.noteCount(cart);

    const pair = this.findPair(cart, intent.ref, intent.rowKey);
    if (!pair.ok) {
      await this.handleRefusal(cart, intent, pair);
      return;
    }

    const sections = this.sectionIds();

    // What the customer asked for, as an AGGREGATE for the group.
    //
    // A clicked row carries the number shown in THAT row's stepper, and a
    // discount partition can show one row of a two-watch group. So the row's
    // number is applied as a DELTA: raising a row of 1 to 3 inside a group of
    // 2 means the group goes to 4, and removing that row means the group goes
    // to 1, not to nothing. With no clicked row the caller is naming the
    // group's absolute quantity.
    let target;
    if (intent.rowKey) {
      const group = (pair.watch.lines.indexOf(pair.row) !== -1) ? pair.watch : pair.fee;
      target = group.total + (intent.qty - pair.row.quantity);
    } else {
      target = intent.qty;
    }
    if (!(target >= 0)) { await this.handleRefusal(cart, intent, { reason: 'row' }); return; }

    // The operation record goes down BEFORE the first write, with the verified
    // equal baseline and the aggregate actually asked for. If a response is
    // lost, this is what tells the next read what the two acceptable outcomes
    // are. Line KEYS are deliberately not recorded — every write can re-key
    // lines that were not even written to, so settle() re-locates the groups
    // from a fresh read.
    const op = {
      ref: intent.ref,
      watchVariantId: Number(pair.watch.lines[0].variant_id),
      target: target,
      base: pair.watch.total,
    };
    this.pendingOp = op;
    this.writePending(op);

    // WATCH first: if the watch cannot move (stock refusal, line gone), the
    // fee must not move either — never charge engraving for a watch the
    // customer could not actually get.
    const wDone = await this.applyGroup(intent.ref, 'watch', target, intent.rowKey, sections);
    if (!wDone.ok) return await this.finishUnverified(op, sections);

    const fDone = await this.applyGroup(intent.ref, 'fee', target, null, sections);
    if (!fDone.ok) return await this.finishUnverified(op, sections);

    // Verify from a FRESH read that both groups stand together at the target.
    // A response body is not evidence: the write that moved one group can have
    // re-keyed and re-partitioned the other.
    let fRes;
    try { fRes = await this.fetchCart(); } catch (e) { return await this.finishUnverified(op, sections); }
    if (!this.usable(fRes)) return await this.finishUnverified(op, sections);
    const settled = this.findPair(fRes, intent.ref, null);
    const wq = settled.watch ? settled.watch.total : 0;
    const fq = settled.fee ? settled.fee.total : 0;
    if (settled.reason === 'ambiguous' || wq !== target || fq !== target) {
      return await this.finishUnverified(op, sections);
    }

    this.clearPending();
    this._retryIntent = null;
    this.clearStatus();
    this.noteCount(fRes);
    await this.emit(fRes);
  }

  // A leg failed, threw, or the response did not show the pair together.
  // Committed-or-not is UNKNOWN, so settle from fresh reads and current keys.
  async finishUnverified(op, sections) {
    const settled = await this.settle(op, sections);
    if (settled === 'intended') {
      this.clearPending();
      this.clearStatus();
    } else if (settled === 'baseline') {
      this.clearPending();
      this.setStatus('unchanged');
    } else if (settled === 'coherent') {
      this.clearPending();
      this.setStatus('settled');
    } else {
      // Still unresolved: the operation stays exclusive, the customer sees it,
      // and the next action can only settle it.
      this.setStatus('unconfirmed', true);
    }
    await this.announceFromFresh();
  }

  // Resolve a recorded operation to the quantity the customer asked for, or
  // failing that to its verified baseline. Both legs are re-located from a
  // FRESH read every attempt — a committed write can have changed nothing but
  // quantities, but a concurrent property edit changes keys, so the keys held
  // before the write cannot be trusted. Unrelated lines are never touched.
  // Returns 'intended', 'baseline', or false (unresolved, stays locked).
  async settle(op, sections) {
    sections = sections || this.sectionIds();
    const targets = op.target === op.base ? [op.target] : [op.target, op.base];
    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      let cart;
      try { cart = await this.fetchCart(); } catch (e) { return false; }
      if (!this.usable(cart)) return false;
      this.noteCount(cart);

      const legs = this.locate(cart, op);
      if (legs.ambiguous) return false;   // duplicate members: never guessed

      let wrote = true;
      if (legs.watchQty !== target) {
        // change.js can move or empty a line; it cannot re-create one.
        if (!legs.watchLines.length) wrote = false;
        else if (!(await this.applyGroup(op.ref, 'watch', target, null, sections)).ok) wrote = false;
      }
      if (wrote && legs.feeQty !== target) {
        if (!legs.feeLines.length) wrote = false;
        else if (!(await this.applyGroup(op.ref, 'fee', target, null, sections)).ok) wrote = false;
      }

      // Believing a response body is what produced the orphan in the first
      // place: the outcome is whatever a fresh read says it is.
      let check;
      try { check = await this.fetchCart(); } catch (e) { return false; }
      if (!this.usable(check)) return false;
      this.noteCount(check);
      const now = this.locate(check, op);
      if (!now.ambiguous && now.watchQty === target && now.feeQty === target) {
        return target === op.target ? 'intended' : 'baseline';
      }
    }
    // Neither number was reached. The lock exists to stop a customer checking
    // out with an orphaned or mismatched pair — so if a fresh read proves the
    // two legs are EQUAL and unambiguous anyway, there is nothing left to
    // protect them from, and holding the bag hostage would be the worse bug.
    // The change simply did not happen, and the status says so.
    try {
      const last = await this.fetchCart();
      if (this.usable(last)) {
        this.noteCount(last);
        const legs = this.locate(last, op);
        if (!legs.ambiguous && legs.watchQty === legs.feeQty) return 'coherent';
      }
    } catch (e) {}
    return false;
  }

  // The two GROUPS of a recorded operation, by whole-ref equality and real
  // roles, aggregated. A missing group reads as quantity 0, which is exactly
  // what a completed removal looks like. Conflicting members inside a role, a
  // foreign line under the ref, or a watch group that is no longer the recorded
  // variant are all ambiguity — recovery guesses at nothing.
  locate(cart, op) {
    const out = { watchLines: [], watchQty: 0, feeLines: [], feeQty: 0, ambiguous: false };
    const found = this.findPair(cart, op.ref, null);
    if (this.blocked(found.reason)) { out.ambiguous = true; return out; }
    if (found.watch && found.watch.lines.length) {
      if (Number(found.watch.lines[0].variant_id) !== Number(op.watchVariantId)) {
        out.ambiguous = true;
        return out;
      }
      out.watchLines = found.watch.lines;
      out.watchQty = found.watch.total;
    }
    if (found.fee && found.fee.lines.length) {
      out.feeLines = found.fee.lines;
      out.feeQty = found.fee.total;
    }
    return out;
  }

  // The identity of a line as a purchasable unit: its variant, its selling plan
  // and its FULL properties. Two lines with the same signature are the same
  // thing bought twice and Shopify may split or merge them freely; two lines
  // that differ are different things and must never be added together.
  // JSON is used because it cannot be forged by a customer-writable property
  // value containing whatever separator we picked.
  lineSig(it) {
    try {
      const p = (it && it.properties) || {};
      const keys = Object.keys(p).sort();
      // EXACT values, not stringified ones: String() would make the value null
      // and the value "null" the same thing, and would flatten a structured
      // value to "[object Object]". JSON keeps them apart. A key whose value is
      // undefined cannot be encoded faithfully, so identity is refused instead.
      const pairs = [];
      for (let i = 0; i < keys.length; i++) {
        if (typeof p[keys[i]] === 'undefined') return null;
        pairs.push([keys[i], p[keys[i]]]);
      }
      const plan = (it && it.selling_plan_allocation && it.selling_plan_allocation.selling_plan
        && it.selling_plan_allocation.selling_plan.id) || null;
      return JSON.stringify([String(it.variant_id), plan, pairs]);
    } catch (e) { return null; }   // cannot prove identity: caller refuses
  }

  // One watch GROUP and one fee GROUP under the WHOLE ref string.
  //
  // A group is every line of that role whose signature is identical — the
  // discount partition case. Its quantity is the aggregate. Two lines of the
  // same role that DISAGREE (different words, different variant, different
  // plan, an extra property) are two different things and are refused, as is
  // any third line carrying the ref that is neither role.
  //
  // The two groups must carry the same engraved words and stand at the same
  // aggregate quantity before anything is written, on every entry.
  findPair(cart, ref, rowKey) {
    const out = { ok: false, watch: null, fee: null, row: null, reason: '' };
    const items = (cart && cart.items) || [];
    const watch = { lines: [], total: 0, sig: null };
    const fee = { lines: [], total: 0, sig: null };
    let ambiguous = false;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const p = it.properties || {};
      if (p['_Engraving Ref'] !== ref) continue;
      let group = null;
      if (Number(it.variant_id) === SERVICE_VARIANT_ID) {
        if (p['Engraving Text'] == null || p['Engraving Text'] === '') { ambiguous = true; continue; }
        group = fee;
      } else if (p['Engraved with'] != null && p['Engraved with'] !== '') {
        group = watch;
      } else {
        // A third line carries this ref and is neither leg. Recovery reads the
        // same cart the same way and would call that ambiguous, so accepting it
        // HERE could start a write that a later failure could never settle —
        // an unrecoverable lock. Refuse before the first write instead.
        ambiguous = true;
        continue;
      }
      const sig = this.lineSig(it);
      if (!sig) { ambiguous = true; continue; }
      if (group.sig === null) group.sig = sig;
      else if (group.sig !== sig) { ambiguous = true; continue; }
      group.lines.push(it);
      group.total += it.quantity;
      if (rowKey && it.key === rowKey) out.row = it;
    }

    // Both groups are always returned, empty ones included: a removal that has
    // taken the watch group to nothing still has to be able to finish the fee
    // group. `ok` and `reason` are what decide whether a NEW action may start.
    out.watch = watch;
    out.fee = fee;
    if (ambiguous) { out.reason = 'ambiguous'; return out; }
    if (!watch.lines.length || !fee.lines.length) { out.reason = 'incomplete'; return out; }
    if (String(watch.lines[0].properties['Engraved with']) !== String(fee.lines[0].properties['Engraving Text'])) {
      out.reason = 'text'; return out;
    }
    if (watch.total !== fee.total) { out.reason = 'unequal'; return out; }
    if (rowKey && !out.row) { out.reason = 'row'; return out; }
    out.ok = true;
    return out;
  }

  // Reasons a group must not be written to, in recovery as much as first time.
  // `unequal` and `incomplete` are the very states recovery exists to resolve,
  // so they do NOT block; ambiguity and a watch/fee word mismatch do. Live
  // words that disagree mean the two groups are not one purchase, and moving
  // their quantities together would be a guess about someone's engraving.
  blocked(reason) {
    return reason === 'ambiguous' || reason === 'text';
  }

  // The ONE write that moves a group one step towards its target. Increases
  // land on the row the customer was looking at; decreases take units from
  // that row first, so removing one row of a split pair removes that row's
  // units and leaves the rest of the group alone.
  nextGroupWrite(lines, total, target, preferKey) {
    if (!lines.length || total === target) return null;
    const ordered = lines.slice();
    if (preferKey) {
      let at = -1;
      for (let i = 0; i < ordered.length; i++) if (ordered[i].key === preferKey) { at = i; break; }
      if (at > 0) ordered.unshift(ordered.splice(at, 1)[0]);
    }
    const line = ordered[0];
    if (target > total) return { key: line.key, quantity: line.quantity + (target - total) };
    const take = Math.min(total - target, line.quantity);
    if (take <= 0) return null;
    return { key: line.key, quantity: line.quantity - take };
  }

  // Move a whole group to an aggregate quantity, ONE line at a time, RE-READING
  // between every write. The real shop merges a split group into a single line
  // as soon as one member moves, and re-keys unrelated lines in the same
  // response — so a plan computed once and executed blind would write to keys
  // that no longer exist. Returns { ok, cart }.
  async applyGroup(ref, role, target, preferKey, sections) {
    let cart = null;
    const maxSteps = 8;
    let prefer = preferKey;
    for (let step = 0; step < maxSteps; step++) {
      try { cart = await this.fetchCart(); } catch (e) { return { ok: false, cart: null }; }
      if (!this.usable(cart)) return { ok: false, cart: null };
      this.noteCount(cart);

      const groups = this.findPair(cart, ref, null);
      const group = role === 'fee' ? groups.fee : groups.watch;
      if (this.blocked(groups.reason)) return { ok: false, cart: cart };
      if (!group) return { ok: false, cart: cart };
      if (group.total === target) return { ok: true, cart: cart };

      const write = this.nextGroupWrite(group.lines, group.total, target, prefer);
      if (!write) return { ok: false, cart: cart };
      const res = await this.changeLine(write.key, write.quantity, sections).catch(() => null);
      if (!res) return { ok: false, cart: cart };
      prefer = null;                     // the clicked row may not survive a merge
    }
    return { ok: false, cart: cart };
  }

  // Nothing is written on a refusal, with ONE exception: a remove click that
  // names an exact row of a malformed pair removes THAT row and nothing else.
  // The customer asked for that line to go and there is no second line to
  // guess at — refusing outright would leave a broken row they cannot clear.
  async handleRefusal(cart, intent, pair) {
    if (intent.qty === 0 && intent.rowKey) {
      const owned = ((cart && cart.items) || []).filter((it) => {
        const p = it.properties || {};
        if (it.key !== intent.rowKey) return false;
        if (p['_Engraving Ref'] !== intent.ref) return false;
        return Number(it.variant_id) === SERVICE_VARIANT_ID
          || (p['Engraved with'] != null && p['Engraved with'] !== '');
      });
      if (owned.length === 1) {
        const res = await this.changeLine(owned[0].key, 0, this.sectionIds()).catch(() => null);
        if (res) {
          this.noteCount(res);
          this.setStatus('removedone');
          await this.emit(res);
          return;
        }
        this.setStatus('refused');
        await this.announceFromFresh();
        return;
      }
    }
    // Re-render the rows so the display shows the truth rather than the
    // quantity the customer asked for, and say why nothing moved.
    // The fresh announcement below already puts the rows back to the truth,
    // so there is nothing a Retry could usefully re-run: the same action would
    // be refused again for the same reason. Message only.
    this.setStatus(pair.reason === 'unequal' ? 'unequal' : 'refused');
    await this.announceFromFresh();
  }

  async fetchCart() {
    const res = await fetch(`${Theme.routes.cart_url}.js`);
    if (!res.ok) throw new Error('cart ' + res.status);
    return res.json();
  }

  async changeLine(key, qty, sections) {
    const body = { id: key, quantity: qty };
    if (sections) body.sections = sections;
    const res = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('change ' + res.status);
    return res.json();
  }

  // A body is only evidence if it actually carries the cart. A response that
  // parsed but has no items array must NEVER be read as "both lines are gone"
  // — at target 0 that is exactly what a missing array would look like.
  usable(payload) {
    return !!(payload && Array.isArray(payload.items));
  }

  qtyIn(payload, key) {
    return ((payload && payload.items) || [])
      .filter((i) => i.key === key)
      .reduce((n, i) => n + i.quantity, 0);
  }

  sectionIds() {
    return Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), (el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  noteCount(payload) {
    const n = payload && typeof payload.item_count === 'number' ? payload.item_count : null;
    if (n != null) this.lastKnownCount = n;
  }

  // The cart section renders its own count in a hidden span; that is the
  // rendered truth and is what the header badge is currently showing.
  knownCount() {
    try {
      const el = document.querySelector('[ref="cartItemCount"]');
      if (el) {
        const n = parseInt(String(el.textContent).trim(), 10);
        if (!isNaN(n)) return n;
      }
    } catch (e) {}
    return this.lastKnownCount;
  }

  async emit(payload) {
    // 🚨 cart-icon.js reads `itemCount ?? 0`, so an event with no count
    // announces an EMPTY cart and blanks the badge. Only ever send a number
    // that was actually read.
    const count = payload && typeof payload.item_count === 'number' && isFinite(payload.item_count)
      ? payload.item_count : null;
    if (count == null) {
      // No trustworthy number in this body: fall back to the rendered truth
      // rather than announcing a cart we cannot count.
      await this.refreshRows();
      return;
    }
    const sections = payload && payload.sections && typeof payload.sections === 'object'
      ? payload.sections : null;
    await this.announceAndSettle({ itemCount: count, sections: sections });
  }

  // Re-render the cart rows when we have no trustworthy payload. The count
  // rides along at its KNOWN value; if nothing at all is known no event is
  // sent, because an absent count reads as zero.
  async refreshRows() {
    const count = this.knownCount();
    if (count == null) { this.paintStatus(); return; }
    await this.announceAndSettle({ itemCount: count, sections: null });
  }

  // 🚨 THE STATUS LINE LIVES INSIDE THE CART SECTION, SO EVERY REFRESH
  // DESTROYS IT. cart-items-component answers a sections-less cart:update by
  // starting sectionRenderer.renderSection() ASYNCHRONOUSLY, which replaces
  // the section — including the error and its Try again — a beat after we
  // painted. Painting on a timer would just be a race with better odds.
  //
  // So the refresh is OWNED here: dispatch, then AWAIT the same render the
  // component started (sectionRenderer dedupes by URL, so this is the very
  // same request, and its own abort/morph handling means one morph, not two),
  // and only then repaint the retained status and re-apply the checkout lock
  // to the freshly rendered controls.
  async announceAndSettle(data) {
    const ids = this.sectionIds();
    const payload = {
      source: 'fw-engraving-cart',
      itemCount: data.itemCount,
      sections: data.sections || null,
    };
    document.dispatchEvent(new CartUpdateEvent({}, 'fw-engraving-cart', payload));

    const waits = [];
    if (data.sections) {
      // Rendered HTML rode the event, so no component starts a fetch of its
      // own; these are the only morphs in flight.
      Object.keys(data.sections).forEach((id) => {
        try { waits.push(morphSection(id, data.sections[id])); } catch (e) {}
      });
    } else if (ids) {
      const renderer = this.sectionRefresher();
      if (renderer) {
        ids.split(',').filter(Boolean).forEach((id) => {
          try { waits.push(renderer.renderSection(id, { cache: false })); } catch (e) {}
        });
      }
    }
    await this.settleAll(waits);
    this.paintStatus();
  }

  // Resolved through `typeof` so the module still runs where the theme's
  // section renderer is not present.
  sectionRefresher() {
    try {
      return typeof sectionRenderer !== 'undefined' && sectionRenderer
        && typeof sectionRenderer.renderSection === 'function' ? sectionRenderer : null;
    } catch (e) { return null; }
  }

  async settleAll(promises) {
    if (!promises || !promises.length) return;
    try {
      await Promise.all(promises.map((p) => Promise.resolve(p).catch(() => {})));
    } catch (e) {}
  }

  // After a failed or untrustworthy write: announce the ACTUAL cart from a
  // fresh read (rows + header count go back to the truth). READ-ONLY.
  async announceFromFresh() {
    let cart = null;
    try {
      // /cart.js takes no `sections` parameter — asking for them there returns
      // the cart with no rendered HTML, which is what made the components go
      // and fetch their own section behind our back.
      const res = await fetch(`${Theme.routes.cart_url}.js`);
      if (res.ok) cart = await res.json();
    } catch (e) { cart = null; }
    if (!this.usable(cart)) { await this.refreshRows(); return; }
    this.noteCount(cart);
    await this.emit(cart);
  }

  /* ---- visible status. CartErrorEvent has no cart-side consumer, so a
     failure that is only dispatched is a failure nobody sees. The strings are
     rendered by snippets/cart-products.liquid onto the status element, so
     they stay translatable and this file holds no copy. ---- */

  statusNodes() {
    try {
      const list = document.querySelectorAll('[data-fw-eng-status]');
      return Array.prototype.slice.call(list);
    } catch (e) { return []; }
  }

  setStatus(kind, retry) {
    this._status = { kind: kind, retry: !!retry };
    this.paintStatus();
  }

  clearStatus() {
    this._status = null;
    this.paintStatus();
  }

  paintStatus() {
    this.applyCheckoutLock();
    const state = this._status;
    this.statusNodes().forEach((node) => {
      const text = node.querySelector ? node.querySelector('[data-fw-eng-status-text]') : null;
      const retry = node.querySelector ? node.querySelector('[data-fw-eng-retry]') : null;
      if (!state) {
        node.hidden = true;
        if (retry) retry.hidden = true;
        return;
      }
      const msg = node.getAttribute('data-fw-msg-' + state.kind) || node.getAttribute('data-fw-msg-refused') || '';
      if (text) text.textContent = msg;
      node.hidden = !msg;
      if (retry) retry.hidden = !state.retry;
    });
  }

  /* ---- pending-operation memory. Only the opaque pair ref, the watch
     variant, and two integers — no engraving words, ever. sessionStorage so
     it dies with the tab; a stale record is harmless because settling always
     re-reads the real cart. ---- */

  // A stored record eventually authorises a WRITE, so every field is checked
  // before it is believed. Anything malformed is dropped, not repaired.
  readPending() {
    let raw = null;
    try { raw = sessionStorage.getItem(PENDING_KEY); } catch (e) { return null; }
    if (!raw) return null;
    let op = null;
    try { op = JSON.parse(raw); } catch (e) { this.writePending(null); return null; }
    const whole = (n) => typeof n === 'number' && isFinite(n) && Math.floor(n) === n && Math.abs(n) <= Number.MAX_SAFE_INTEGER;
    const variant = op && Number(op.watchVariantId);
    const good = !!op
      && typeof op.ref === 'string' && op.ref.length > 0 && op.ref.length <= 64
      && whole(op.target) && op.target >= 0
      && whole(op.base) && op.base > 0
      && whole(variant) && variant > 0 && variant !== SERVICE_VARIANT_ID;
    if (!good) { this.writePending(null); return null; }
    return { ref: op.ref, watchVariantId: variant, target: op.target, base: op.base };
  }

  writePending(op) {
    try {
      if (op) sessionStorage.setItem(PENDING_KEY, JSON.stringify(op));
      else sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {}
  }

  clearPending() {
    this.pendingOp = null;
    this.writePending(null);
  }

  // On a revisit, an operation left unverified is checked READ-ONLY. If the
  // pair is now coherent at either acceptable quantity it is simply closed;
  // otherwise the lock holds and the customer is offered the retry. Nothing
  // is written on page load — a passive repair is exactly what caused the
  // fee-deleted-mid-edit incident.
  async reviewPendingOnLoad() {
    const op = this.pendingOp;
    if (!op) return;
    // Paint FIRST. The operation is unresolved right now, so the message, the
    // Try again and the checkout lock are true right now. If the read below
    // fails or comes back malformed, the customer still sees why the bag is
    // held and what to press — silence plus a dead checkout button is the
    // worst of both.
    this.setStatus('unconfirmed', true);
    let cart;
    try { cart = await this.fetchCart(); } catch (e) { return; }
    if (!this.usable(cart)) return;
    this.noteCount(cart);
    const legs = this.locate(cart, op);
    // Equal legs, no duplicates: the pair is sound, so whatever happened to
    // that operation it left nothing broken behind. Release the bag.
    if (!legs.ambiguous && legs.watchQty === legs.feeQty) {
      this.clearPending();
      this.clearStatus();
    }
  }

  // The visible Retry always does something: it settles an unresolved
  // operation, re-runs an intent whose cart read failed, or — with neither —
  // re-reads the bag and clears the message.
  async retryPending() {
    if (this.busy) return;
    if (this.pdpLocked()) {
      const held = this.queued || this._retryIntent;
      if (held) { this.queued = held; this._retryIntent = null; this.waitForPdp(); }
      this.setStatus('wait');
      return;
    }
    if (!this.pendingOp) {
      const again = this._retryIntent;
      if (again) {
        this._retryIntent = null;
        this.clearStatus();
        this.request(again.ref, again.qty, again.rowKey);
        return;
      }
      this.busy = true;
      this.applyCheckoutLock();
      try { await this.announceFromFresh(); this.clearStatus(); }
      catch (e) { this.setStatus('read', true); }
      finally { this.busy = false; this.applyCheckoutLock(); this.drainBatch(); }
      return;
    }
    this.busy = true;
    this.applyCheckoutLock();
    const op = this.pendingOp;
    try {
      const settled = await this.settle(op);
      if (settled === 'intended') { this.clearPending(); this._retryIntent = null; this.clearStatus(); }
      else if (settled === 'baseline') { this.clearPending(); this._retryIntent = null; this.setStatus('unchanged'); }
      else if (settled === 'coherent') { this.clearPending(); this._retryIntent = null; this.setStatus('settled'); }
      else this.setStatus('unconfirmed', true);
      await this.announceFromFresh();
    } catch (e) {
      this.setStatus('unconfirmed', true);
    } finally {
      this.busy = false;
      this.applyCheckoutLock();
      const next = this.queued;
      if (next) { this.queued = null; this.request(next.ref, next.qty, next.rowKey); return; }
      this.drainBatch();
    }
  }

  // Read-only reconciliation pass (kept for callers/import parity): reports
  // the truth, never writes. Customer refs are untrusted strings, so grouping
  // uses a Map with whole-string keys — an ordinary object would let a
  // "__proto__"-shaped ref write onto Object.prototype.
  async reconcile() {
    let cart;
    try {
      cart = await this.fetchCart();
    } catch (e) {
      return;
    }
    const groups = new Map();
    (cart.items || []).forEach((it) => {
      const r = (it.properties || {})['_Engraving Ref'];
      if (typeof r !== 'string' || !r) return;
      if (!groups.has(r)) groups.set(r, { watch: null, fee: null });
      const g = groups.get(r);
      const p = it.properties || {};
      if (Number(it.variant_id) === SERVICE_VARIANT_ID) {
        if (!g.fee) g.fee = it;
      } else if (p['Engraved with'] != null && p['Engraved with'] !== '') {
        g.watch = it;
      }
    });
    // No repairs: orphan fees and drifted quantities are surfaced only through
    // the honest itemCount/rows below. The PDP engine and an explicit customer
    // action own every mutation.
    this.noteCount(cart);
    await this.emit(cart);
  }
}

if (typeof window !== 'undefined') {
  const start = () => {
    if (!window.__fwEngravingCart) {
      window.__fwEngravingCart = new FWEngravingCart();
    }
    window.__fwEngravingCart.attach();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
